// Features: MCRO001-F0027, MCRO001-F0029 (inherits MCRO001-F0007/F0008/F0021)
// Tests: T-MCRO-027, T-MCRO-029
// Presentation-only additions for MCRO001-F0037 / T-MCRO-037: `mirrored`,
// `stagePositionLabel` and the swipe-scrub layer. None of them touches projection,
// priority, the media frame or the count.
// MCRO001-F0085 / T-MCRO-085 (choreo issue #39): a media-degraded candidate
// (`degraded: 'media-loading' | 'media-unavailable'`) keeps its place in
// priority resolution but playback affordances that need media are withheld —
// no <video> is mounted for it and its focus-on notice is suppressed; the
// stage states honestly that the map is present while its media is not.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import RehearsalStageSwipe from './RehearsalStageSwipe';
import { resolveDemonstrationPriority } from './demonstrationPriority';
import {
  mappedDemonstrationAuthorityKey,
  projectMappedDemonstration,
  readApprovedMappedDemonstrations,
} from './mappedDemonstrationProjection';

const genericFailure = 'Demonstration backdrop unavailable. Rehearsal remains available.';

export default function MappedDemonstrationBackdrop({
  boundary,
  organisationId,
  routineId,
  revisionId,
  count,
  bpm,
  rehearsalRate,
  playing,
  explicitFocusId = '',
  explicitFocusAuthorityKey = '',
  selectedRehearsalRate = rehearsalRate,
  focusOnRateThreshold,
  onActivateFocus,
  onFocusUnavailable,
  onProjectionChange,
  // MCRO001-F0037: presentation-only inputs. `mirrored` flips the video element's
  // rendered pixels and nothing else; `stagePositionLabel` re-presents the position
  // MCRO001-F0007 already derived; `onStageSeek`/`stageSeekBounds` render the swipe
  // layer, which calls the same count-setting path the scrub slider calls.
  mirrored = false,
  stagePositionLabel = '',
  onStageSeek = null,
  stageSeekBounds = null,
  children,
}) {
  const videoRef = useRef(null);
  const lastProjectionRef = useRef(null);
  const focusFailureRef = useRef('');
  const [failedId, setFailedId] = useState('');
  const [unsupportedRateKey, setUnsupportedRateKey] = useState('');
  const [notice, setNotice] = useState('');
  const [activatingId, setActivatingId] = useState('');
  // Re-read live authority every render. Withdrawal and moderation fail closed even
  // when the injected #920 adapter keeps a stable object identity.
  const approved = readApprovedMappedDemonstrations(boundary, {
    organisationId,
    routineId,
    revisionId,
  });
  const candidates = approved;
  const clearExplicitFocus = useCallback(() => {
    if (!explicitFocusId) return;
    const focusKey = `${explicitFocusId}:${explicitFocusAuthorityKey}`;
    if (focusFailureRef.current === focusKey) return;
    focusFailureRef.current = focusKey;
    onFocusUnavailable?.();
  }, [explicitFocusAuthorityKey, explicitFocusId, onFocusUnavailable]);
  useEffect(() => {
    focusFailureRef.current = '';
  }, [explicitFocusAuthorityKey, explicitFocusId]);
  useEffect(() => {
    const activeCandidate = approved.find(({ demonstrationId }) => demonstrationId === explicitFocusId);
    if (explicitFocusId && (!activeCandidate || (explicitFocusAuthorityKey
      && mappedDemonstrationAuthorityKey(activeCandidate) !== explicitFocusAuthorityKey))) {
      clearExplicitFocus();
    }
  }, [approved, clearExplicitFocus, explicitFocusAuthorityKey, explicitFocusId]);
  const resolution = useMemo(() => resolveDemonstrationPriority(candidates, {
    count,
    bpm,
    rehearsalRate: selectedRehearsalRate,
    revisionId,
    explicitFocusId,
    supportsPlaybackRate: boundary?.supportsPlaybackRate,
    // MCRO001-F0029: demonstrationPriority.js's focusOnClassification reads
    // context.focusOnRateThreshold (falling back to its own
    // DEFAULT_FOCUS_ON_RATE_THRESHOLD = 1.3 when absent/non-finite/<=0). See
    // MusicChoreography.js's capabilities.focusOnRateThreshold for where
    // this value is sourced from the served capabilities payload.
    focusOnRateThreshold,
  }), [bpm, boundary?.supportsPlaybackRate, candidates, count, explicitFocusId, focusOnRateThreshold, revisionId, selectedRehearsalRate]);
  const selected = resolution.selected;
  const selectedAuthorityKey = mappedDemonstrationAuthorityKey(selected);
  // T-MCRO-085 case A: a media-degraded selection withholds every playback
  // affordance — no <video> is ever mounted without a confirmed playable
  // rendition, so the stage can never imply media it does not have.
  const mediaWithheld = Boolean(selected?.degraded);
  const mediaWithheldMessage = selected?.degraded === 'media-loading'
    ? 'Demonstration map present — media loading, showing timing counts only'
    : 'Demonstration map present — media unavailable, showing timing counts only';
  const focusNotices = resolution.focusOn.filter(({ candidate }) => candidate.demonstrationId !== explicitFocusId
    && !candidate.degraded);
  const projection = useMemo(() => selected ? projectMappedDemonstration({
    timingMap: selected.timingMap,
    count,
    bpm,
    rehearsalRate,
    revisionId,
    allowExplicitFocusRate: explicitFocusId === selected.demonstrationId,
  }) : { visible: false }, [bpm, count, explicitFocusId, rehearsalRate, revisionId, selected]);
  let supportsRate = projection.visible;
  if (projection.visible && typeof boundary?.supportsPlaybackRate === 'function') {
    try {
      supportsRate = boundary.supportsPlaybackRate(projection.effectiveVideoRate) === true;
    } catch (_) {
      supportsRate = false;
    }
  }
  const rateKey = selected && projection.visible ? `${selected.demonstrationId}:${projection.effectiveVideoRate}` : '';
  const fallback = projection.visible && (!supportsRate || unsupportedRateKey === rateKey);
  const visible = projection.visible && !mediaWithheld && selected?.demonstrationId !== failedId;

  useEffect(() => {
    setFailedId('');
    setUnsupportedRateKey('');
  }, [selectedAuthorityKey]);

  // T-MCRO-027/T-MCRO-029: report the honest, count-derived effective video rate
  // upward (never a render-time side effect) so Rehearsal can show it next to the
  // speed control. This is observational only — it never influences projection,
  // count or playback, and it fails closed (visible:false) whenever the backdrop
  // itself is not currently showing a video, including on unmount.
  // MCRO001-F0036/T-MCRO-036 case F: also report whether the live boundary
  // currently resolves any approved candidate at all (`hasCandidates`), reusing
  // the exact same `approved` read this component already performs every render.
  // This is the only honest signal for "no map has ever been saved for this actor
  // on this revision" as distinct from "a map exists but is inactive right now" —
  // Rehearsal's no-map hint must never invent a second, potentially stale read.
  // `mappedRange` (case A) is the same selected/visible timing map's own
  // startCount/endCount, purely for the scrub timeline's shaded span — it is never
  // a second source of the media frame or count.
  const mappedStartCount = visible ? selected.timingMap.startCount : null;
  const mappedEndCount = visible ? selected.timingMap.endCount : null;
  useEffect(() => {
    if (typeof onProjectionChange !== 'function') return undefined;
    const mappedRange = visible ? { startCount: mappedStartCount, endCount: mappedEndCount } : null;
    onProjectionChange(visible
      ? { visible: true, effectiveVideoRate: projection.effectiveVideoRate, fallback, hasCandidates: true, mappedRange }
      : { visible: false, effectiveVideoRate: null, fallback: false, hasCandidates: approved.length > 0, mappedRange: null });
    return () => onProjectionChange({ visible: false, effectiveVideoRate: null, fallback: false, hasCandidates: false, mappedRange: null });
  }, [approved.length, fallback, mappedEndCount, mappedStartCount, onProjectionChange, projection.effectiveVideoRate, visible]);

  const failMedia = useCallback((demonstrationId) => {
    setFailedId(demonstrationId);
    if (demonstrationId === explicitFocusId) clearExplicitFocus();
  }, [clearExplicitFocus, explicitFocusId]);

  const attachVideo = useCallback((node) => {
    if (!node && videoRef.current) {
      videoRef.current.pause?.();
      lastProjectionRef.current = null;
    }
    videoRef.current = node;
  }, []);

  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (!visible) {
      video.pause?.();
      return undefined;
    }
    try {
      video.muted = true;
      const previous = lastProjectionRef.current;
      const authoritativePositionChanged = !previous
        || previous.demonstrationId !== selected.demonstrationId
        || previous.count !== count;
      if (authoritativePositionChanged) video.currentTime = projection.mediaMs / 1000;
      if (!fallback && !projection.atInclusiveEnd) {
        try {
          video.playbackRate = projection.effectiveVideoRate;
        } catch (_) {
          video.pause?.();
          setUnsupportedRateKey(rateKey);
          lastProjectionRef.current = { demonstrationId: selected.demonstrationId, count };
          return undefined;
        }
      }
      if (fallback || projection.atInclusiveEnd || !playing) video.pause?.();
      else video.play?.()?.catch?.(() => failMedia(selected.demonstrationId));
    } catch (_) {
      failMedia(selected.demonstrationId);
    }
    lastProjectionRef.current = { demonstrationId: selected.demonstrationId, count };
    return undefined;
  }, [count, failMedia, fallback, playing, projection.atInclusiveEnd, projection.effectiveVideoRate, projection.mediaMs, rateKey, selected, visible]);

  useEffect(() => () => videoRef.current?.pause?.(), []);

  const activateFocus = async ({ candidate }) => {
    if (activatingId) return;
    setActivatingId(candidate.demonstrationId);
    setNotice('');
    try {
      if (typeof onActivateFocus !== 'function') throw new Error('focus activation unavailable');
      const latestApproved = readApprovedMappedDemonstrations(boundary, {
        organisationId,
        routineId,
        revisionId,
      });
      const latestCandidate = latestApproved.find(({ demonstrationId }) => demonstrationId === candidate.demonstrationId);
      if (!latestCandidate || mappedDemonstrationAuthorityKey(latestCandidate)
        !== mappedDemonstrationAuthorityKey(candidate)) throw new Error('focus authority changed');
      const latestResolution = resolveDemonstrationPriority(latestApproved, {
        count,
        bpm,
        rehearsalRate: selectedRehearsalRate,
        revisionId,
        explicitFocusId,
        supportsPlaybackRate: boundary?.supportsPlaybackRate,
      });
      const latestItem = latestResolution.focusOn.find(({ candidate: item }) => item.demonstrationId === candidate.demonstrationId);
      if (!latestItem) throw new Error('focus classification changed');
      const activated = await onActivateFocus(latestItem);
      if (activated === false) throw new Error('focus activation rejected');
      setNotice(latestItem.classification.reasons.includes('rate')
        ? 'Focus-on video opened from its beginning at 1x video speed.'
        : 'Focus-on video opened from its beginning for this rehearsal session.');
    } catch (_) {
      setNotice('The focus-on video could not be opened. Rehearsal timing was not changed.');
    } finally {
      setActivatingId('');
    }
  };

  if (approved.length === 0) return children;

  return <>
    {focusNotices.length > 0 && <section className="mc-focus-on-notices" aria-label="Focus-on videos">
      {focusNotices.map((item) => <button
        type="button"
        className="mc-focus-on-notice"
        key={item.candidate.demonstrationId}
        disabled={Boolean(activatingId) || typeof onActivateFocus !== 'function'}
        onClick={() => activateFocus(item)}
      ><strong>Focus-on video</strong><span>{item.candidate.sequenceLabel}</span><em>Click to see</em></button>)}
    </section>}
    <div
      className={`mc-demonstration-stage${visible ? ' active' : ''}${fallback ? ' fallback' : ''}`}
      data-projection-state={visible ? (fallback ? 'frame-projection' : 'video') : 'none'}
      data-effective-video-rate={projection.visible ? projection.effectiveVideoRate : undefined}
    >
      {visible && <video
        ref={attachVideo}
        className={`mc-demonstration-video${mirrored ? ' mirrored' : ''}`}
        src={selected.rendition.source}
        muted
        playsInline
        preload="auto"
        aria-label={`Demonstrated sequence: ${selected.sequenceLabel}`}
        onError={() => failMedia(selected.demonstrationId)}
      />}
      {/* MCRO001-F0037 cases G/I: the swipe layer exists only over a video that is
          actually rendering, so it can never imply a backdrop that is not there. */}
      {visible && typeof onStageSeek === 'function' && Number.isFinite(Number(stageSeekBounds?.min))
        && Number.isFinite(Number(stageSeekBounds?.max)) && <RehearsalStageSwipe
        min={Number(stageSeekBounds.min)}
        max={Number(stageSeekBounds.max)}
        count={count}
        onSeek={onStageSeek}
      />}
      {visible && <div className="mc-demonstration-label" role="status">Demonstrated sequence · {selected.sequenceLabel}</div>}
      {/* MCRO001-F0037 case F: the same section/bar/beat value rendered below the
          stage, repeated inside it so the dancer never looks away. It is
          aria-hidden because the below-stage readout already announces it. */}
      {visible && stagePositionLabel
        && <div className="mc-stage-position" aria-hidden="true">{stagePositionLabel}</div>}
      {visible && fallback && <div className="mc-demonstration-fallback" role="status">Count-synchronised frame projection</div>}
      {!visible && mediaWithheld && <div className="mc-demonstration-fallback" role="status">{mediaWithheldMessage}</div>}
      {!visible && focusNotices.length > 0 && <div className="mc-focus-on-withheld" role="status">Focus-on video available · playback withheld at this rehearsal speed or priority</div>}
      {children}
    </div>
    {notice && <div className="mc-visually-hidden" role="status">{notice}</div>}
    {failedId && <div className="mc-visually-hidden" role="status">{genericFailure}</div>}
  </>;
}
